import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useSearchViewMemory } from './SearchViewMemoryContext';
import { searchView } from './searchViewMemory';

// Wait for delayed content to be tall enough; never keep fighting a user's scroll.
export function restoreSearchScroll(target, done = () => {}) {
  let stopped = false,
    timer;
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(attempt);
  const deadline = Date.now() + 5000;
  function stop() {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    observer?.disconnect();
    for (const event of ['wheel', 'touchstart', 'keydown'])
      window.removeEventListener(event, stop, true);
    done();
  }
  function attempt() {
    if (stopped) return;
    clearTimeout(timer);
    const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
    const available = Math.max(0, height - window.innerHeight);
    window.scrollTo({ top: Math.min(target, available), behavior: 'auto' });
    if ((available >= target && Math.abs(window.scrollY - target) <= 1) || Date.now() >= deadline)
      stop();
    else timer = setTimeout(attempt, 100);
  }
  for (const event of ['wheel', 'touchstart', 'keydown'])
    window.addEventListener(event, stop, { capture: true, passive: true });
  observer?.observe(document.body);
  observer?.observe(document.documentElement);
  attempt();
  return stop;
}
export function useSearchScrollMemory(editing = false) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const memory = useSearchViewMemory();
  const current = useRef(null);
  const mode = editing ? 'assist' : 'idle';
  useLayoutEffect(() => {
    const previous = current.current;
    if (previous?.memory === memory) memory.save(previous.view, previous.y);
    const view = searchView(location, mode);
    if (!view) {
      current.current = { view: null, memory };
      return;
    }
    const y = memory.position(
      view,
      previous?.memory === memory ? previous.view : null,
      navigationType,
    );
    const entry = { view, memory, y, restoring: true };
    current.current = entry;
    const previousPolicy = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    const save = () => {
      if (entry.restoring) return;
      entry.y = window.scrollY;
      memory.save(view, entry.y);
    };
    // Capture link/focus transitions before the outgoing document is replaced/clamped.
    window.addEventListener('scroll', save, { passive: true });
    window.addEventListener('click', save, true);
    window.addEventListener('focusin', save, true);
    const stop = restoreSearchScroll(y, () => {
      entry.restoring = false;
      entry.y = window.scrollY;
      memory.save(view, entry.y);
    });
    return () => {
      stop();
      window.removeEventListener('scroll', save);
      window.removeEventListener('click', save, true);
      window.removeEventListener('focusin', save, true);
      window.history.scrollRestoration = previousPolicy;
    };
  }, [location, mode, navigationType, memory]);
}
